<script lang="ts" setup>
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import {
  useCredentials,
  useUploadCredential,
  useGenerateCredential,
  useStartCredentialCsr,
  useRevokeCredential,
} from "@/queries/credentials";
import { useAlertStore } from "@/stores/alerts-store";
import {
  credentialKindLabel,
  credentialKindIcon,
  credentialBadgeClass,
  credentialBadgeLabel,
  daysUntilExpiry,
  expiryClass,
  expiryLabel,
} from "../utils/credentialDisplay";
import DataView from "@/components/DataView.vue";
import PageHeader from "@/components/PageHeader.vue";
import SkeletonLoader from "@/components/SkeletonLoader.vue";
import FormAlert from "@/components/FormAlert.vue";
import PanelGrid from "@/views/shared/components/PanelGrid.vue";
import PanelCard from "@/views/shared/components/PanelCard.vue";
import type { CredentialKind, CredentialListItem } from "@/types";
import type { Config } from "datatables.net";

const router = useRouter();
const alertStore = useAlertStore();

const { credentials, credentialsLoading: loading, credentialsError: error, refetchCredentials } = useCredentials();
const { uploadCredential, uploadCredentialPending, uploadCredentialError, uploadCredentialReset } = useUploadCredential();
const { generateCredential, generateCredentialPending, generateCredentialError, generateCredentialReset } = useGenerateCredential();
const { startCsr, startCsrPending, startCsrError, startCsrReset } = useStartCredentialCsr();
const { revokeCredential } = useRevokeCredential();

const searchQuery = ref("");
const selectedKind = ref("all");

const filteredCredentials = computed(() => {
  let list = credentials.value;
  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase();
    list = list.filter((c) =>
      c.name.toLowerCase().includes(q) ||
      (c.subject ?? "").toLowerCase().includes(q) ||
      (c.fingerprint ?? "").toLowerCase().includes(q));
  }
  if (selectedKind.value !== "all") list = list.filter((c) => c.kind === selectedKind.value);
  return list;
});

const stats = computed(() => {
  const list = filteredCredentials.value;
  return {
    total: list.length,
    inUse: list.filter((c) => c.usageCount > 0).length,
    expiringSoon: list.filter((c) => { const d = daysUntilExpiry(c.notAfter); return d !== null && d < 30; }).length,
    pendingCsr: list.filter((c) => c.status === "PendingCsr").length,
  };
});

// Expiry banner over the unfiltered set: danger when anything is inside 7 days (or expired),
// warning inside 30. One banner, worst case wins.
const expiring = computed(() =>
  credentials.value
    .map((c) => ({ c, days: daysUntilExpiry(c.notAfter) }))
    .filter((e): e is { c: CredentialListItem; days: number } => e.days !== null && e.days < 30));
const expiryBanner = computed(() => {
  if (expiring.value.length === 0) return null;
  const urgent = expiring.value.some((e) => e.days < 7);
  const names = expiring.value.map((e) => e.c.name).join(", ");
  return {
    class: urgent ? "alert-danger" : "alert-warning",
    text: `${expiring.value.length === 1 ? "1 credential expires" : `${expiring.value.length} credentials expire`} within 30 days: ${names}.`,
  };
});

const hasFilters = computed(() => !!searchQuery.value || selectedKind.value !== "all");
function clearFilters() {
  searchQuery.value = "";
  selectedKind.value = "all";
}

const tableOptions = {
  paging: true,
  pageLength: 10,
  stateSave: true,
  columns: [
    { data: "name", title: "Name", render: "#nameCell" },
    { data: "kind", title: "Kind", render: "#kindCell" },
    { data: "status", title: "Status", orderable: false, searchable: false, render: "#statusCell" },
    { data: "notAfter", title: "Expires", render: "#expiryCell" },
    { data: "usageCount", title: "In use", render: "#usageCell" },
    { data: "createdAt", title: "Created", render: (d: string) => (d ? new Date(d).toLocaleDateString() : "") },
    { data: null, title: "", orderable: false, searchable: false, className: "text-end", render: "#actionsCell" },
  ],
  layout: { topEnd: "" },
} as Config;

function openCredential(id: string) {
  router.push(`/connections/credentials/${id}`);
}

function remove(c: CredentialListItem) {
  if (!confirm(`Revoke "${c.name}"? It will be removed and can no longer be linked to connections.`)) return;
  revokeCredential(c.id, {
    onSuccess: () => alertStore.addSuccess("Revoked", `${c.name} has been revoked.`),
    onError: (e) => alertStore.addError("Revoke failed", e.userMessage),
  });
}

// ── Create modal — three flows: generate in place, BYO upload, CSR ceremony (X509 only) ────────

type CreateFlow = "generate" | "upload" | "csr";

const showCreate = ref(false);
const flow = ref<CreateFlow>("generate");
const form = ref({
  name: "",
  kind: "X509" as CredentialKind,
  // generate — X509
  subject: "",
  sans: "",
  validityDays: 365,
  // generate — SSH / PGP
  sshAlgorithm: "ed25519",
  comment: "",
  pgpUserId: "",
  // upload — X509
  pfxBase64: "",
  pfxFileName: "",
  password: "",
  // upload — SSH / PGP
  privateKey: "",
  publicKey: "",
  passphrase: "",
});
// A completed CSR start switches the modal body to the handoff panel (CSR text + copy).
const startedCsr = ref<{ credentialId: string; csr: string } | null>(null);

const createPending = computed(() =>
  uploadCredentialPending.value || generateCredentialPending.value || startCsrPending.value);
const createError = computed(() =>
  uploadCredentialError.value ?? generateCredentialError.value ?? startCsrError.value);

function resetCreateErrors() {
  uploadCredentialReset();
  generateCredentialReset();
  startCsrReset();
}

function openCreate() {
  form.value = {
    name: "", kind: "X509", subject: "", sans: "", validityDays: 365,
    sshAlgorithm: "ed25519", comment: "", pgpUserId: "",
    pfxBase64: "", pfxFileName: "", password: "", privateKey: "", publicKey: "", passphrase: "",
  };
  flow.value = "generate";
  startedCsr.value = null;
  resetCreateErrors();
  showCreate.value = true;
}

function closeCreate() {
  showCreate.value = false;
}

function setKind(kind: CredentialKind) {
  form.value.kind = kind;
  if (kind !== "X509" && flow.value === "csr") flow.value = "generate";
  resetCreateErrors();
}

function parseSans(): string[] {
  return form.value.sans.split(",").map((s) => s.trim()).filter((s) => s.length > 0);
}

function onPfxFile(event: Event) {
  const file = (event.target as HTMLInputElement).files?.[0];
  if (!file) { form.value.pfxBase64 = ""; form.value.pfxFileName = ""; return; }
  const reader = new FileReader();
  reader.onload = () => {
    // readAsDataURL yields "data:...;base64,<payload>" — the API wants the bare payload.
    form.value.pfxBase64 = String(reader.result).split(",")[1] ?? "";
    form.value.pfxFileName = file.name;
  };
  reader.readAsDataURL(file);
}

const canSubmit = computed(() => {
  const f = form.value;
  if (!f.name.trim()) return false;
  if (flow.value === "generate") {
    if (f.kind === "X509") return !!f.subject.trim();
    if (f.kind === "PgpKey") return !!f.pgpUserId.trim();
    return true;
  }
  if (flow.value === "upload") {
    if (f.kind === "X509") return !!f.pfxBase64;
    return !!f.privateKey.trim() && !!f.publicKey.trim();
  }
  return !!f.subject.trim(); // csr
});

function submitCreate() {
  const f = form.value;
  const done = (title: string, message: string) => {
    closeCreate();
    alertStore.addSuccess(title, message);
  };

  if (flow.value === "generate") {
    generateCredential(
      {
        name: f.name.trim(),
        kind: f.kind,
        subject: f.kind === "X509" ? f.subject.trim() : null,
        sans: f.kind === "X509" ? parseSans() : null,
        validityDays: f.kind === "X509" ? f.validityDays : null,
        sshAlgorithm: f.kind === "SshKey" ? f.sshAlgorithm : null,
        comment: f.kind === "SshKey" && f.comment.trim() ? f.comment.trim() : null,
        pgpUserId: f.kind === "PgpKey" ? f.pgpUserId.trim() : null,
      },
      { onSuccess: (c) => done("Generated", `${c.name} is ready to link.`) });
  } else if (flow.value === "upload") {
    const fields: Record<string, string> =
      f.kind === "X509"
        ? { pfx: f.pfxBase64, ...(f.password ? { password: f.password } : {}) }
        : { privateKey: f.privateKey, publicKey: f.publicKey.trim(), ...(f.passphrase ? { passphrase: f.passphrase } : {}) };
    uploadCredential(
      { name: f.name.trim(), kind: f.kind, fields },
      { onSuccess: (c) => done("Uploaded", `${c.name} is ready to link.`) });
  } else {
    startCsr(
      { name: f.name.trim(), subject: f.subject.trim(), sans: parseSans() },
      { onSuccess: (r) => { startedCsr.value = { credentialId: r.credential.id, csr: r.csr }; } });
  }
}

async function copyCsr() {
  if (!startedCsr.value) return;
  await navigator.clipboard.writeText(startedCsr.value.csr);
  alertStore.addSuccess("Copied", "CSR copied to clipboard.");
}
</script>

<template>
  <main>
    <PanelGrid :columns="1">
      <PanelCard>
        <PageHeader
          title="Credentials"
          :breadcrumbs="[{ label: 'Dashboard', href: '/' }, { label: 'Connections', href: '/connections' }, { label: 'Credentials' }]"
        >
          <template #actions>
            <button class="bb-btn-primary-cta" @click="openCreate" data-cy="new-credential">
              <i class="bi bi-plus"></i> New Credential
            </button>
          </template>
        </PageHeader>

        <SkeletonLoader v-if="loading || error" type="table" :error="error" @retry="refetchCredentials" />

        <template v-else>
          <div v-if="expiryBanner" class="alert d-flex align-items-center gap-2" :class="expiryBanner.class">
            <i class="bi bi-exclamation-triangle"></i>
            <span>{{ expiryBanner.text }}</span>
          </div>

          <!-- Status at a glance -->
          <div class="row g-3 mb-4">
            <div class="col-6 col-md-3">
              <div class="card text-center"><div class="card-body">
                <div class="fs-3 fw-bold mb-2">{{ stats.total }}</div>
                <div class="text-muted small">Total</div>
              </div></div>
            </div>
            <div class="col-6 col-md-3">
              <div class="card text-center"><div class="card-body">
                <div class="fs-3 fw-bold text-success mb-2">{{ stats.inUse }}</div>
                <div class="text-muted small">In use</div>
              </div></div>
            </div>
            <div class="col-6 col-md-3">
              <div class="card text-center"><div class="card-body">
                <div class="fs-3 fw-bold mb-2" :class="{ 'text-warning': stats.expiringSoon > 0 }">{{ stats.expiringSoon }}</div>
                <div class="text-muted small">Expiring soon</div>
              </div></div>
            </div>
            <div class="col-6 col-md-3">
              <div class="card text-center"><div class="card-body">
                <div class="fs-3 fw-bold mb-2" :class="{ 'text-warning': stats.pendingCsr > 0 }">{{ stats.pendingCsr }}</div>
                <div class="text-muted small">Awaiting certificate</div>
              </div></div>
            </div>
          </div>

          <!-- Search + kind filter (client-side; search spans every row) -->
          <div class="mb-4">
            <div class="row g-3">
              <div class="col-md-4">
                <div class="input-group">
                  <span class="input-group-text"><i class="bi bi-search"></i></span>
                  <input type="text" class="form-control" placeholder="Search credentials..." v-model="searchQuery" data-cy="search-credentials" />
                </div>
              </div>
              <div class="col-md-3">
                <select class="form-select" v-model="selectedKind" data-cy="filter-kind">
                  <option value="all">All kinds</option>
                  <option value="X509">Certificate (X.509)</option>
                  <option value="SshKey">SSH key</option>
                  <option value="PgpKey">PGP key</option>
                </select>
              </div>
            </div>
            <div v-if="hasFilters" class="mt-2">
              <button class="bb-btn-secondary-cta btn-sm" @click="clearFilters"><i class="bi bi-x me-1"></i>Clear filters</button>
            </div>
          </div>

          <DataView
            table-id="credentials-table"
            :data="filteredCredentials"
            view-mode="table"
            :options="tableOptions"
            @row-click="(c: CredentialListItem) => openCredential(c.id)"
          >
            <template #nameCell="{ rowData: c }">
              <div class="d-flex align-items-center gap-2">
                <i :class="credentialKindIcon(c.kind)" class="fs-5 text-primary"></i>
                <div>
                  <div>{{ c.name }}</div>
                  <div class="small text-muted font-monospace">{{ c.subject ?? c.fingerprint }}</div>
                </div>
              </div>
            </template>
            <template #kindCell="{ rowData: c }">
              {{ credentialKindLabel(c.kind) }}
            </template>
            <template #statusCell="{ rowData: c }">
              <span class="badge" :class="credentialBadgeClass(c.status)">{{ credentialBadgeLabel(c.status) }}</span>
            </template>
            <template #expiryCell="{ rowData: c }">
              <span class="small" :class="expiryClass(c.notAfter)">{{ expiryLabel(c.notAfter) }}</span>
            </template>
            <template #usageCell="{ rowData: c }">
              <span v-if="c.usageCount > 0">{{ c.usageCount }} connection{{ c.usageCount === 1 ? "" : "s" }}</span>
              <span v-else class="text-muted">Unused</span>
            </template>
            <template #actionsCell="{ rowData: c }">
              <div class="d-flex gap-2 justify-content-end">
                <button class="bb-btn-danger btn-sm text-nowrap" @click.stop="remove(c)"><i class="bi bi-trash me-1"></i>Revoke</button>
              </div>
            </template>

            <template #empty-state>
              <div class="text-center py-5">
                <i class="bi bi-shield-lock display-1 text-muted"></i>
                <h2 class="mt-3 text-muted">{{ hasFilters ? "No matching credentials" : "No credentials yet" }}</h2>
                <p class="text-muted">{{ hasFilters ? "Try adjusting your filters or search." : "Add a certificate or keypair to link into your connections." }}</p>
                <button v-if="!hasFilters" class="bb-btn-primary-cta" @click="openCreate"><i class="bi bi-plus"></i> New Credential</button>
                <button v-else class="bb-btn-secondary-cta" @click="clearFilters"><i class="bi bi-x me-1"></i>Clear filters</button>
              </div>
            </template>
          </DataView>
        </template>
      </PanelCard>
    </PanelGrid>

    <!-- Create modal -->
    <div
      class="modal fade"
      :class="{ show: showCreate }"
      :style="{ display: showCreate ? 'block' : 'none' }"
      @click.self="closeCreate"
    >
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h2 class="modal-title">{{ startedCsr ? "Signing request created" : "New credential" }}</h2>
            <button type="button" class="btn-close" @click="closeCreate"></button>
          </div>

          <!-- CSR handoff: the key is held server-side; the user takes this text to their CA. -->
          <div v-if="startedCsr" class="modal-body">
            <p>
              Send this signing request to your certificate authority. When the signed certificate
              comes back, open the credential and paste it there to activate.
            </p>
            <textarea class="form-control font-monospace" rows="10" readonly :value="startedCsr.csr"></textarea>
          </div>
          <div v-if="startedCsr" class="modal-footer">
            <button type="button" class="bb-btn-secondary-cta" @click="copyCsr"><i class="bi bi-clipboard me-1"></i>Copy CSR</button>
            <button type="button" class="bb-btn-primary-cta" @click="openCredential(startedCsr.credentialId)">
              Open credential <i class="bi bi-arrow-right ms-1"></i>
            </button>
          </div>

          <form v-if="!startedCsr" @submit.prevent="submitCreate">
            <div class="modal-body">
              <FormAlert :error="createError" @dismiss="resetCreateErrors" />

              <div class="mb-3">
                <label class="form-label" for="credential-name">Name</label>
                <input id="credential-name" type="text" class="form-control" v-model="form.name" placeholder="e.g. Acme mTLS client" required />
              </div>

              <div class="mb-3">
                <label class="form-label">Kind</label>
                <div class="d-flex gap-2" role="group" aria-label="Credential kind">
                  <button
                    v-for="kind in (['X509', 'SshKey', 'PgpKey'] as CredentialKind[])"
                    :key="kind"
                    type="button"
                    class="bb-btn-secondary-cta flex-fill text-nowrap"
                    :class="{ active: form.kind === kind }"
                    @click="setKind(kind)"
                  >
                    <i :class="credentialKindIcon(kind)" class="me-1"></i>{{ credentialKindLabel(kind) }}
                  </button>
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label">Source</label>
                <div class="d-flex gap-2" role="group" aria-label="Credential source">
                  <button type="button" class="bb-btn-secondary-cta flex-fill text-nowrap" :class="{ active: flow === 'generate' }" @click="flow = 'generate'; resetCreateErrors()">
                    Generate
                  </button>
                  <button type="button" class="bb-btn-secondary-cta flex-fill text-nowrap" :class="{ active: flow === 'upload' }" @click="flow = 'upload'; resetCreateErrors()">
                    Upload existing
                  </button>
                  <button
                    v-if="form.kind === 'X509'"
                    type="button"
                    class="bb-btn-secondary-cta flex-fill text-nowrap"
                    :class="{ active: flow === 'csr' }"
                    @click="flow = 'csr'; resetCreateErrors()"
                  >
                    CA-signed (CSR)
                  </button>
                </div>
                <div class="form-text">
                  <template v-if="flow === 'generate'">Create the key material here. X.509 certificates are self-signed.</template>
                  <template v-else-if="flow === 'upload'">Bring your own material. It is encrypted at rest and never shown again.</template>
                  <template v-else>Generate a key here and get a signing request for your certificate authority.</template>
                </div>
              </div>

              <!-- Generate / CSR — X509 -->
              <template v-if="(flow === 'generate' && form.kind === 'X509') || flow === 'csr'">
                <div class="mb-3">
                  <label class="form-label" for="credential-subject">Subject</label>
                  <input id="credential-subject" type="text" class="form-control" v-model="form.subject" placeholder="e.g. acme-client or CN=acme-client" />
                </div>
                <div class="mb-3">
                  <label class="form-label" for="credential-sans">Subject alternative names <span class="text-muted">(optional, comma-separated)</span></label>
                  <input id="credential-sans" type="text" class="form-control" v-model="form.sans" placeholder="api.acme.com, acme.com" />
                </div>
                <div v-if="flow === 'generate'" class="mb-3">
                  <label class="form-label" for="credential-validity">Valid for (days)</label>
                  <input id="credential-validity" type="number" class="form-control" v-model.number="form.validityDays" min="1" max="3650" />
                </div>
              </template>

              <!-- Generate — SSH -->
              <template v-if="flow === 'generate' && form.kind === 'SshKey'">
                <div class="mb-3">
                  <label class="form-label" for="credential-algorithm">Algorithm</label>
                  <select id="credential-algorithm" class="form-select" v-model="form.sshAlgorithm">
                    <option value="ed25519">ed25519 (recommended)</option>
                    <option value="rsa-4096">RSA 4096</option>
                  </select>
                </div>
                <div class="mb-3">
                  <label class="form-label" for="credential-comment">Comment <span class="text-muted">(optional)</span></label>
                  <input id="credential-comment" type="text" class="form-control" v-model="form.comment" placeholder="e.g. sftp@acme" />
                </div>
              </template>

              <!-- Generate — PGP -->
              <template v-if="flow === 'generate' && form.kind === 'PgpKey'">
                <div class="mb-3">
                  <label class="form-label" for="credential-pgp-userid">User id</label>
                  <input id="credential-pgp-userid" type="text" class="form-control" v-model="form.pgpUserId" placeholder="Acme Ops <ops@acme.com>" />
                </div>
              </template>

              <!-- Upload — X509 -->
              <template v-if="flow === 'upload' && form.kind === 'X509'">
                <div class="mb-3">
                  <label class="form-label" for="credential-pfx">PKCS#12 file (.pfx / .p12)</label>
                  <input id="credential-pfx" type="file" class="form-control" accept=".pfx,.p12" @change="onPfxFile" />
                  <div v-if="form.pfxFileName" class="form-text">{{ form.pfxFileName }}</div>
                </div>
                <div class="mb-3">
                  <label class="form-label" for="credential-password">Password <span class="text-muted">(if the file has one)</span></label>
                  <input id="credential-password" type="password" class="form-control" v-model="form.password" autocomplete="off" />
                </div>
              </template>

              <!-- Upload — SSH / PGP -->
              <template v-if="flow === 'upload' && form.kind !== 'X509'">
                <div class="mb-3">
                  <label class="form-label" for="credential-private-key">Private key</label>
                  <textarea
                    id="credential-private-key" class="form-control font-monospace" rows="5" v-model="form.privateKey"
                    :placeholder="form.kind === 'SshKey' ? '-----BEGIN OPENSSH PRIVATE KEY-----' : '-----BEGIN PGP PRIVATE KEY BLOCK-----'"
                  ></textarea>
                </div>
                <div class="mb-3">
                  <label class="form-label" for="credential-public-key">Public key</label>
                  <textarea
                    id="credential-public-key" class="form-control font-monospace" :rows="form.kind === 'SshKey' ? 2 : 5" v-model="form.publicKey"
                    :placeholder="form.kind === 'SshKey' ? 'ssh-ed25519 AAAA... comment' : '-----BEGIN PGP PUBLIC KEY BLOCK-----'"
                  ></textarea>
                </div>
                <div class="mb-3">
                  <label class="form-label" for="credential-passphrase">Passphrase <span class="text-muted">(if the private key has one)</span></label>
                  <input id="credential-passphrase" type="password" class="form-control" v-model="form.passphrase" autocomplete="off" />
                </div>
              </template>
            </div>
            <div class="modal-footer">
              <button type="button" class="bb-btn-cancel" @click="closeCreate">Cancel</button>
              <button type="submit" class="bb-btn-primary-cta" :disabled="createPending || !canSubmit">
                <span v-if="createPending" class="spinner-border spinner-border-sm me-2" role="status"></span>
                {{ flow === "csr" ? "Create signing request" : flow === "upload" ? "Upload" : "Generate" }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
    <div v-if="showCreate" class="modal-backdrop fade show"></div>
  </main>
</template>
